import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Key, Store, Users, CheckCircle, XCircle, Clock, Info } from 'lucide-react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'

const JoinStorePage = () => {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!code || code.trim().length !== 12) {
      setError('El código debe tener 12 caracteres')
      return
    }

    try {
      setLoading(true)
      const response = await api.post('/auth/invitation-codes/use', { code: code.toUpperCase() })
      
      if (response.data.success) {
        toast.success(`¡Te has unido exitosamente a ${response.data.data.store.name}!`)
        // Redirigir al perfil o dashboard del empleado
        setTimeout(() => {
          navigate('/profile')
          window.location.reload() // Recargar para actualizar el contexto de autenticación
        }, 1500)
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error al usar el código de invitación'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Unirse a un Negocio</h1>
          <p className="text-gray-600">
            Ingresa el código de invitación que te proporcionó el dueño del negocio
          </p>
        </div>

        {/* Information Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Info className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">¿Cómo funciona?</h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>El dueño del negocio te proporcionará un código de 12 caracteres</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>El código tiene una vigencia de 1 minuto</span>
                </li>
                <li className="flex items-start gap-2">
                  <Users className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Una vez unido, el dueño asignará tus roles (despachador, chef, delivery)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Store className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Podrás acceder al panel de empleado del negocio</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                Código de Invitación
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Key className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
                    if (value.length <= 12) {
                      setCode(value)
                      setError(null)
                    }
                  }}
                  placeholder="ABCD1234EFGH"
                  maxLength={12}
                  className={`w-full pl-12 pr-4 py-4 border-2 rounded-xl text-lg font-mono tracking-wider text-center focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    error ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
              </div>
              {error && (
                <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                  <XCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}
              <p className="mt-2 text-sm text-gray-500">
                Ingresa el código de 12 caracteres (solo letras y números)
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 12}
              className="w-full py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Procesando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Unirse al Negocio
                </>
              )}
            </button>
          </form>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            ¿No tienes un código? Contacta al dueño del negocio para obtener uno.
          </p>
        </div>
      </div>
    </div>
  )
}

export default JoinStorePage

